/**
 * Trading Session Detection Utility
 *
 * Determines which major forex trading session a trade belongs to,
 * based on the trade's open time and the user's timezone.
 *
 * Session hours (UTC):
 *   Sydney:    22:00 – 07:00  (wraps midnight)
 *   Asia:      00:00 – 09:00
 *   London:    07:00 – 16:00
 *   New York:  12:00 – 21:00
 *
 * When sessions overlap we pick the "dominant" session for that hour:
 *   00-03  → Asia
 *   03-07  → Asia
 *   07-08  → London
 *   08-12  → London
 *   12-16  → New York  (London/NY overlap → NY gets priority)
 *   16-21  → New York
 *   21-00  → Sydney
 */

export const TRADING_SESSIONS = {
  Sydney:   { label: 'Sydney',   color: '#f59e0b', hours: '22:00–07:00 UTC', icon: '🌏' },
  Asia:     { label: 'Asia',     color: '#ef4444', hours: '00:00–09:00 UTC', icon: '🌏' },
  London:   { label: 'London',   color: '#3b82f6', hours: '07:00–16:00 UTC', icon: '🌍' },
  'New York': { label: 'New York', color: '#22c55e', hours: '12:00–21:00 UTC', icon: '🌎' },
};

export const SESSION_COLORS = {
  Sydney: '#f59e0b',
  Asia: '#ef4444',
  London: '#3b82f6',
  'New York': '#22c55e',
};

/**
 * Get the trading session for a given UTC hour
 * @param {number} utcHour - Hour in UTC (0-23)
 * @returns {string} - Session name
 */
export function getSessionFromUTCHour(utcHour) {
  if (utcHour >= 0 && utcHour < 3) return 'Asia';
  if (utcHour >= 3 && utcHour < 7) return 'Asia';
  if (utcHour >= 7 && utcHour < 8) return 'London';
  if (utcHour >= 8 && utcHour < 12) return 'London';
  if (utcHour >= 12 && utcHour < 16) return 'New York';
  if (utcHour >= 16 && utcHour < 21) return 'New York';
  if (utcHour >= 21) return 'Sydney';
  return 'Asia';
}

/**
 * Detect the trading session from a trade's open time
 * @param {string} openTime - ISO date string of trade open time
 * @returns {string|null} - Session name or null if no open time
 */
export function detectTradingSession(openTime) {
  if (!openTime) return null;
  const date = new Date(openTime);
  if (isNaN(date.getTime())) return null;
  return getSessionFromUTCHour(date.getUTCHours());
}

/**
 * Get all sessions that are active at a given UTC hour (for overlap display)
 * @param {number} utcHour - Hour in UTC (0-23)
 * @returns {string[]} - Array of active session names
 */
export function getActiveSessionsAtHour(utcHour) {
  const sessions = [];
  // Sydney: 22-07 UTC
  if (utcHour >= 22 || utcHour < 7) sessions.push('Sydney');
  // Asia: 00-09 UTC
  if (utcHour >= 0 && utcHour < 9) sessions.push('Asia');
  // London: 07-16 UTC
  if (utcHour >= 7 && utcHour < 16) sessions.push('London');
  // New York: 12-21 UTC
  if (utcHour >= 12 && utcHour < 21) sessions.push('New York');
  return sessions;
}

/**
 * Convert a UTC hour to the user's local hour based on their timezone
 * @param {number} utcHour - Hour in UTC
 * @param {string} timezone - IANA timezone string (e.g. 'America/New_York')
 * @returns {number} - Local hour (0-23)
 */
export function utcHourToLocal(utcHour, timezone) {
  try {
    const date = new Date();
    date.setUTCHours(utcHour, 0, 0, 0);
    const localStr = date.toLocaleString('en-US', { timeZone: timezone, hour: 'numeric', hour12: false });
    return parseInt(localStr, 10);
  } catch {
    return utcHour;
  }
}

/**
 * Get session schedule in the user's local timezone
 * @param {string} timezone - IANA timezone string
 * @returns {Object} - Session info with local hours
 */
export function getSessionScheduleForTimezone(timezone) {
  return {
    Sydney: {
      ...TRADING_SESSIONS.Sydney,
      localStart: utcHourToLocal(22, timezone),
      localEnd: utcHourToLocal(7, timezone),
    },
    Asia: {
      ...TRADING_SESSIONS.Asia,
      localStart: utcHourToLocal(0, timezone),
      localEnd: utcHourToLocal(9, timezone),
    },
    London: {
      ...TRADING_SESSIONS.London,
      localStart: utcHourToLocal(7, timezone),
      localEnd: utcHourToLocal(16, timezone),
    },
    'New York': {
      ...TRADING_SESSIONS['New York'],
      localStart: utcHourToLocal(12, timezone),
      localEnd: utcHourToLocal(21, timezone),
    },
  };
}

/**
 * Duration buckets for the duration summary table
 * Each bucket has a label, min (inclusive) and max (exclusive) in seconds
 */
export const DURATION_BUCKETS = [
  { label: 'Under 1 min',       min: 0,      max: 60 },
  { label: '1:00 to 1:59',      min: 60,     max: 120 },
  { label: '2:00 to 4:59',      min: 120,    max: 300 },
  { label: '5:00 to 9:59',      min: 300,    max: 600 },
  { label: '10:00 to 29:59',    min: 600,    max: 1800 },
  { label: '30:00 to 59:59',    min: 1800,   max: 3600 },
  { label: '1:00:00 to 1:59:59', min: 3600,  max: 7200 },
  { label: '2:00:00 to 3:59:59', min: 7200,  max: 14400 },
  { label: '4:00:00 and over',  min: 14400,  max: Infinity },
];

/**
 * Compute duration-based summary stats for trades
 * Groups trades by hold duration into buckets like the prop-firm summary table
 * @param {Array} trades - Array of trade objects with openTime, closeTime, profit
 * @returns {Array} - Array of bucket stats: { label, tradeCount, netProfit, totalProfits, winCount, lossCount, winPercent }
 */
export function computeDurationSummary(trades) {
  return DURATION_BUCKETS.map(bucket => {
    const bucketTrades = trades.filter(t => {
      if (!t.openTime || !t.closeTime) return false;
      const open = new Date(t.openTime).getTime();
      const close = new Date(t.closeTime).getTime();
      if (isNaN(open) || isNaN(close)) return false;
      const durationSec = (close - open) / 1000;
      return durationSec >= bucket.min && durationSec < bucket.max;
    });

    const winTrades = bucketTrades.filter(t => Number(t.profit) > 0);
    const netProfit = bucketTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);
    const totalProfits = winTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);

    return {
      label: bucket.label,
      tradeCount: bucketTrades.length,
      netProfit,
      totalProfits,
      winCount: winTrades.length,
      lossCount: bucketTrades.filter(t => Number(t.profit) < 0).length,
      winPercent: bucketTrades.length > 0 ? (winTrades.length / bucketTrades.length) * 100 : null,
    };
  });
}

/**
 * Compute session-based performance summary
 * @param {Array} trades - Array of trade objects
 * @returns {Array} - Array of session stats
 */
export function computeSessionSummary(trades) {
  const sessions = ['Sydney', 'Asia', 'London', 'New York'];
  return sessions.map(session => {
    const sessionTrades = trades.filter(t => {
      if (t.trading_session) return t.trading_session === session;
      return detectTradingSession(t.openTime) === session;
    });

    const winTrades = sessionTrades.filter(t => Number(t.profit) > 0);
    const lossTrades = sessionTrades.filter(t => Number(t.profit) < 0);
    const totalProfit = sessionTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);
    const grossProfit = winTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);
    const grossLoss = Math.abs(lossTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0));
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;

    // Average holding time in minutes
    const holdTimes = sessionTrades
      .filter(t => t.openTime && t.closeTime)
      .map(t => (new Date(t.closeTime) - new Date(t.openTime)) / 60000);
    const avgHoldTime = holdTimes.length > 0
      ? holdTimes.reduce((a, b) => a + b, 0) / holdTimes.length
      : 0;

    return {
      session,
      color: SESSION_COLORS[session],
      tradeCount: sessionTrades.length,
      winCount: winTrades.length,
      lossCount: lossTrades.length,
      winPercent: sessionTrades.length > 0 ? (winTrades.length / sessionTrades.length) * 100 : 0,
      totalProfit,
      profitFactor,
      avgHoldTimeMin: avgHoldTime,
    };
  });
}

/**
 * Common IANA timezone list for the profile selector
 */
export const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'America/Toronto', label: 'Eastern Time (Canada)' },
  { value: 'America/Sao_Paulo', label: 'Brasilia Time' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)' },
  { value: 'Europe/Moscow', label: 'Moscow Time' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
  { value: 'Africa/Johannesburg', label: 'South Africa Time' },
  { value: 'Africa/Lagos', label: 'West Africa Time' },
  { value: 'Africa/Cairo', label: 'Eastern European Time' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time' },
  { value: 'Asia/Bangkok', label: 'Indochina Time' },
  { value: 'Asia/Singapore', label: 'Singapore Time' },
  { value: 'Asia/Shanghai', label: 'China Standard Time' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time' },
  { value: 'Australia/Perth', label: 'Australian Western Time' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time' },
];
